import React from 'react';
import {
  Box,
  Typography,
  Breadcrumbs,
  Link,
  Container,
  Grid,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  Home as HomeIcon,
  Description as DocumentIcon,
  TableChart as DatasetIcon,
  Upload as UploadIcon,
  Analytics as AnalyticsIcon,
  LibraryBooks as TemplateIcon,
  Edit as EditIcon,
  ContentCopy as CopyIcon,
  AutoFixHigh as WizardIcon,
  NavigateNext as StepIcon,
  CheckCircle as CompleteIcon,
  Science as SandboxIcon,
  BugReport as TestIcon,
  Preview as PreviewIcon,
  Notes as NotesIcon,
} from '@mui/icons-material';

export const DeliverablesAdminPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        {/* Breadcrumb */}
        <Breadcrumbs sx={{ mb: 3 }}>
          <Link
            color="inherit"
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Link
            color="inherit"
            href="/admin/hub"
            onClick={(e) => {
              e.preventDefault();
              navigate('/admin/hub');
            }}
            sx={{ cursor: 'pointer' }}
          >
            Admin Hub
          </Link>
          <Typography color="text.primary">Deliverables Admin</Typography>
        </Breadcrumbs>

        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Deliverables Administration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage deliverables systems and workflows.
          </Typography>
        </Box>

        {/* Main Content */}
        <Grid container spacing={3}>
          {/* Document Identifier System Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <DocumentIcon color="primary" fontSize="large" />
                    <Typography variant="h6" component="h2">
                      Document Identifier System
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Process Excel construction documents, identify data sets, and manage document workflows.
                    Upload XLSX files, identify data ranges, and extract structured information.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight="bold">Features:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<UploadIcon />} label="File Upload" size="small" />
                      <Chip icon={<DatasetIcon />} label="Data Extraction" size="small" />
                      <Chip icon={<AnalyticsIcon />} label="Analytics" size="small" />
                    </Stack>
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/admin/deliverables/doc-id-system')}
                      startIcon={<DocumentIcon />}
                      fullWidth
                    >
                      Open Document Identifier System
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Deliverables Templates Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <TemplateIcon color="primary" fontSize="large" />
                    <Typography variant="h6" component="h2">
                      Deliverables Templates
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Create and manage reusable templates for deliverables. Define standard formats, sections, and configurations.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight="bold">Features:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<EditIcon />} label="Template Editor" size="small" />
                      <Chip icon={<CopyIcon />} label="Duplicate Templates" size="small" />
                      <Chip icon={<TemplateIcon />} label="Library" size="small" />
                    </Stack>
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/admin/deliverables/templates')}
                      startIcon={<TemplateIcon />}
                      fullWidth
                    >
                      Manage Templates
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Deliverables Wizards Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <WizardIcon color="primary" fontSize="large" />
                    <Typography variant="h6" component="h2">
                      Deliverables Wizards
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Step-by-step wizards for creating and configuring deliverables. Guided workflows for complex processes.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight="bold">Features:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<StepIcon />} label="Step-by-Step" size="small" />
                      <Chip icon={<WizardIcon />} label="Guided Process" size="small" />
                      <Chip icon={<CompleteIcon />} label="Validation" size="small" />
                    </Stack>
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/admin/deliverables/wizards')}
                      startIcon={<WizardIcon />}
                      fullWidth
                    >
                      Launch Wizards
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Deliverables Sandbox Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <SandboxIcon color="primary" fontSize="large" />
                    <Typography variant="h6" component="h2">
                      Deliverables Sandbox
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Test and experiment with deliverables features in a safe environment. Validate changes before production.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight="bold">Features:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<TestIcon />} label="Testing" size="small" />
                      <Chip icon={<PreviewIcon />} label="Preview" size="small" />
                      <Chip icon={<SandboxIcon />} label="Safe Environment" size="small" />
                    </Stack>
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/admin/deliverables/sandbox')}
                      startIcon={<SandboxIcon />}
                      fullWidth
                    >
                      Open Sandbox
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          {/* Property Blurb Prompts Card */}
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Stack spacing={2}>
                  <Box display="flex" alignItems="center" gap={2}>
                    <NotesIcon color="primary" fontSize="large" />
                    <Typography variant="h6" component="h2">
                      Property Blurb Prompts
                    </Typography>
                    <Chip label="Active" color="success" size="small" />
                  </Box>

                  <Typography variant="body2" color="text.secondary">
                    Manage pre-defined prompts for generating property description blurbs using LLM. Create, edit, and organize reusable prompts.
                  </Typography>

                  <Divider />

                  <Stack spacing={1}>
                    <Typography variant="body2" fontWeight="bold">Features:</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      <Chip icon={<NotesIcon />} label="Prompt Library" size="small" />
                      <Chip icon={<EditIcon />} label="Edit Prompts" size="small" />
                      <Chip icon={<CopyIcon />} label="Examples" size="small" />
                    </Stack>
                  </Stack>

                  <Box sx={{ pt: 1 }}>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/admin/deliverables/property-blurb-prompts')}
                      startIcon={<NotesIcon />}
                      fullWidth
                    >
                      Manage Prompts
                    </Button>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};
